/**
 * The parts every catalog tree shares: the folding state, the header with its
 * fold-everything buttons, and the checkbox that can say "partly".
 *
 * Two selectors sit on top of this. One picks questions and rolls a tick up to
 * the topic above it; the other picks topics and capabilities, where a tick
 * means that node and nothing else. They differ in what a tick means, not in
 * how the box looks, so only the meaning lives in the components.
 */
import {
	createEffect,
	createMemo,
	createSignal,
	type JSX,
	Show,
} from "solid-js";
import type { TreeTopic } from "../api/domain.ts";
import { useI18n } from "../i18n/provider.tsx";
import { IconButton } from "./Form.tsx";
import {
	ChevronDownIcon,
	ChevronRightIcon,
	FoldIcon,
	UnfoldIcon,
} from "./icons.tsx";
import styles from "./TreeSelect.module.scss";

/** Checked, indeterminate, or neither, which is all a checkbox can say. */
export type Fill = "none" | "some" | "all";

export const fillOf = (
	members: readonly string[],
	selected: ReadonlySet<string>,
): Fill => {
	if (members.length === 0) return "none";
	const hits = members.filter((id) => selected.has(id)).length;
	if (hits === 0) return "none";
	return hits === members.length ? "all" : "some";
};

/**
 * A checkbox that can also say "partly". `indeterminate` is a property with no
 * attribute behind it, so it has to be written to the element itself.
 */
export const TriCheckbox = (props: {
	fill: Fill;
	disabled?: boolean;
	onToggle: (on: boolean) => void;
}) => {
	let control!: HTMLInputElement;
	createEffect(() => void (control.indeterminate = props.fill === "some"));

	return (
		<input
			ref={control}
			type="checkbox"
			checked={props.fill === "all"}
			disabled={props.disabled === true}
			onChange={(event) => props.onToggle(event.currentTarget.checked)}
		/>
	);
};

/**
 * Which topics are unfolded. Every topic starts open: enough to start picking,
 * short enough to scan.
 *
 * The default is derived rather than snapshotted, because a tree can arrive
 * after the folding does. On the catalog screen the topics are still being
 * fetched when this runs, and taking them once would leave every branch shut
 * with no hint that anything was ever meant to be open. Once the reader folds
 * something themselves their choice stands, and the default stops deciding.
 *
 * `hasBranch` decides which topics get a twisty at all, and it belongs to the
 * caller because it depends on what that selector renders. A tree that shows
 * only questions must not offer to unfold a topic holding nothing but
 * capabilities: the twisty would open onto nothing.
 */
export const useFolding = (
	topics: () => readonly TreeTopic[],
	hasBranch: (topic: TreeTopic) => boolean = (topic) =>
		topic.questions.length > 0 || topic.capabilities.length > 0,
) => {
	const [chosen, setChosen] = createSignal<ReadonlySet<string> | undefined>(
		undefined,
	);

	const open = createMemo<ReadonlySet<string>>(
		() => chosen() ?? new Set(topics().map((topic) => topic.id)),
	);

	const foldables = createMemo(() =>
		topics()
			.filter(hasBranch)
			.map((topic) => topic.id),
	);

	const isExpanded = (id: string) => open().has(id);

	return {
		hasBranch,
		foldables,
		isExpanded,
		openCount: () => foldables().filter(isExpanded).length,
		toggle: (id: string) => {
			const next = new Set(open());
			if (next.has(id)) next.delete(id);
			else next.add(id);
			setChosen(next);
		},
		/** Unfolds one topic, for a caller that has to reveal something inside it. */
		expand: (id: string) => {
			if (open().has(id)) return;
			setChosen(new Set(open()).add(id));
		},
		expandAll: () => setChosen(new Set(foldables())),
		collapseAll: () => setChosen(new Set<string>()),
	};
};

export type Folding = ReturnType<typeof useFolding>;

/** The twisty in front of a topic, or the gap that keeps rows aligned without one. */
export const Twisty = (props: { topic: TreeTopic; folding: Folding }) => {
	const i18n = useI18n();
	const open = () => props.folding.isExpanded(props.topic.id);

	return (
		<Show when={props.folding.hasBranch(props.topic)}>
			<button
				class={styles.twisty}
				type="button"
				title={open() ? i18n.t().tree.collapse : i18n.t().tree.expand}
				aria-label={open() ? i18n.t().tree.collapse : i18n.t().tree.expand}
				aria-expanded={open()}
				onClick={() => props.folding.toggle(props.topic.id)}
			>
				<Show when={open()} fallback={<ChevronRightIcon />}>
					<ChevronDownIcon />
				</Show>
			</button>
		</Show>
	);
};

/** True when the topic is unfolded and has something to show. */
export const showsBranch = (topic: TreeTopic, folding: Folding): boolean =>
	folding.hasBranch(topic) && folding.isExpanded(topic.id);

export type TreeFrameProps = {
	/** The left of the header: a select-all row, a hint, or nothing. */
	lead?: JSX.Element;
	/** How much is picked, phrased by the caller because only it knows of what. */
	summary: string;
	folding: Folding;
	/** Shown instead of the tree when there is nothing to pick. */
	empty?: string;
	/** False when the tree has no rows, which also disables the fold buttons. */
	filled: boolean;
	children: JSX.Element;
};

/**
 * The box a tree is picked in. It scrolls at a fixed height: a catalog is long
 * enough that letting it grow would push whatever follows off the screen.
 */
export const TreeFrame = (props: TreeFrameProps) => {
	const i18n = useI18n();

	return (
		<div class={styles.select}>
			<div class={styles.head}>
				<Show when={props.lead !== undefined} fallback={<span />}>
					{props.lead}
				</Show>
				<div class={styles.headActions}>
					<span class={styles.summary}>{props.summary}</span>
					<IconButton
						label={i18n.t().tree.expandAll}
						icon={<UnfoldIcon />}
						disabled={
							props.folding.openCount() === props.folding.foldables().length
						}
						onClick={props.folding.expandAll}
					/>
					<IconButton
						label={i18n.t().tree.collapseAll}
						icon={<FoldIcon />}
						disabled={props.folding.openCount() === 0}
						onClick={props.folding.collapseAll}
					/>
				</div>
			</div>

			<div class={styles.scroller}>
				<Show
					when={props.filled}
					fallback={
						<p class={styles.empty}>{props.empty ?? i18n.t().tree.empty}</p>
					}
				>
					<ul class={styles.branch}>{props.children}</ul>
				</Show>
			</div>
		</div>
	);
};

/** Re-exported so the selectors do not each reach for the shared stylesheet. */
export { styles as treeStyles };
